import { SUPPORT_CASE_CHANNELS, SUPPORT_CASE_STATUSES } from "@wallyo/contracts/console/cases";
import type { SupportCaseListItem } from "@wallyo/contracts/console/cases";
import { t, type MessageId } from "../../i18n";
import { ErrorNotice } from "../../shell/ErrorNotice";
import { shortId } from "../audit-format";
import { CHANNEL_ICONS, CHANNEL_LABELS, STATUS_LABELS, customerLabel, deadlinePill, ownerLabel } from "./case-format";
import { QUEUE_VIEWS, casesHref, type QueueFilters, type QueueView } from "./case-route";
import type { QueueState } from "./queue-state";

const VIEW_LABELS: Record<QueueView, MessageId> = {
  all: "cases.view.all",
  unassigned: "cases.view.unassigned",
  mine: "cases.view.mine",
  late: "cases.view.late",
};

export type CaseQueueViewProps = {
  filters: QueueFilters;
  state: QueueState;
  now: Date;
  selectedId: string | null;
  canOpenCase: boolean;
  onFilters: (next: QueueFilters) => void;
  onRetry: () => void;
  onLoadMore: () => void;
  onNewCall: () => void;
};

/** The shared queue: tabs, filters, and one row per case with its live deadline. */
export function CaseQueueView(props: CaseQueueViewProps) {
  const { filters } = props;
  const set = (change: Partial<QueueFilters>) => props.onFilters({ ...filters, ...change });
  return (
    <section className="card case-queue" aria-label={t("cases.queue.aria")}>
      <div className="btns queue-top">
        {props.canOpenCase ? (
          <button className="btn primary" type="button" onClick={props.onNewCall}>
            {t("cases.newCall")}
          </button>
        ) : null}
      </div>
      <div className="tabs" role="tablist" aria-label={t("cases.queue.views")}>
        {QUEUE_VIEWS.map((view) => (
          <button
            key={view}
            type="button"
            role="tab"
            aria-selected={filters.view === view}
            className={`tab${filters.view === view ? " on" : ""}`}
            onClick={() => set({ view })}
          >
            {t(VIEW_LABELS[view])}
          </button>
        ))}
      </div>
      <div className="queue-filters">
        <div className="field">
          <label htmlFor="case-status">{t("cases.filter.status")}</label>
          <select
            id="case-status"
            value={filters.status ?? ""}
            onChange={(event) => set({ status: (event.target.value || null) as QueueFilters["status"] })}
          >
            <option value="">{t("cases.filter.notClosed")}</option>
            {SUPPORT_CASE_STATUSES.map((status) => (
              <option key={status} value={status}>
                {t(STATUS_LABELS[status])}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="case-channel">{t("cases.filter.channel")}</label>
          <select
            id="case-channel"
            value={filters.channel ?? ""}
            onChange={(event) => set({ channel: (event.target.value || null) as QueueFilters["channel"] })}
          >
            <option value="">{t("cases.filter.anyChannel")}</option>
            {SUPPORT_CASE_CHANNELS.map((channel) => (
              <option key={channel} value={channel}>
                {`${CHANNEL_ICONS[channel]} ${t(CHANNEL_LABELS[channel])}`}
              </option>
            ))}
          </select>
        </div>
      </div>
      {filters.userId ? (
        <div className="chips">
          <span className="chip">
            {t("cases.filter.customer", { id: shortId(filters.userId) })}{" "}
            <a
              className="chip-clear"
              href={casesHref({ ...filters, userId: null })}
              aria-label={t("cases.filter.clear")}
            >
              ×
            </a>
          </span>
        </div>
      ) : null}
      <QueueBody {...props} />
    </section>
  );
}

function QueueBody({ state, now, filters, selectedId, onRetry, onLoadMore }: CaseQueueViewProps) {
  if (state.status === "loading" && state.cases.length === 0) {
    return (
      <div className="empty" role="status" aria-live="polite">
        <span className="spinner" aria-hidden="true" />
        <span>{t("cases.queue.loading")}</span>
      </div>
    );
  }
  if (state.status === "error" && state.cases.length === 0) {
    return (
      <div className="audit-error">
        <p className="foot">{t("cases.queue.loadFailed")}</p>
        <ErrorNotice error={state.error} />
        <div>
          <button className="btn primary" type="button" onClick={onRetry}>
            {t("cases.retry")}
          </button>
        </div>
      </div>
    );
  }
  if (state.cases.length === 0) return <div className="empty">{t("cases.queue.empty")}</div>;
  return (
    <>
      {state.status === "error" ? <ErrorNotice error={state.error} /> : null}
      <div className="table-wrap">
        <table className="case-list">
          <thead>
            <tr>
              <th>{t("cases.col.case")}</th>
              <th className="num">{t("cases.col.deadline")}</th>
            </tr>
          </thead>
          <tbody>
            {state.cases.map((item) => (
              <QueueRow
                key={item.supportCaseId}
                item={item}
                now={now}
                href={casesHref(filters, item.supportCaseId)}
                selected={item.supportCaseId === selectedId}
              />
            ))}
          </tbody>
        </table>
      </div>
      <div className="audit-more">
        {state.moreError ? <ErrorNotice error={state.moreError} /> : null}
        {state.nextCursor ? (
          <div className="btns">
            <button className="btn" type="button" onClick={onLoadMore} disabled={state.loadingMore}>
              {state.loadingMore ? t("cases.loadingMore") : state.moreError ? t("cases.retry") : t("cases.loadMore")}
            </button>
          </div>
        ) : null}
      </div>
    </>
  );
}

function QueueRow({
  item,
  now,
  href,
  selected,
}: {
  item: SupportCaseListItem;
  now: Date;
  href: string;
  selected: boolean;
}) {
  const pill = deadlinePill(item, now);
  return (
    <tr
      data-case={item.supportCaseId}
      className={
        [selected ? "sel" : "", item.status === "closed" ? "inactive" : ""].filter(Boolean).join(" ") || undefined
      }
    >
      <td>
        <a className="row-link case-ref" href={href} aria-current={selected ? "true" : undefined}>
          <b className="mono">{item.reference}</b>
        </a>
        {" · "}
        {/* An access case has no channel: it says what it is instead. */}
        {item.channel === null ? (
          <span className="channel">{t("cases.kind.access")}</span>
        ) : (
          <span className="channel" title={t(CHANNEL_LABELS[item.channel])}>
            <span aria-hidden="true">{CHANNEL_ICONS[item.channel]}</span> {t(CHANNEL_LABELS[item.channel])}
          </span>
        )}
        <br />
        <bdi className={item.customer ? "case-who" : "case-who unknown"}>{customerLabel(item)}</bdi>
        <br />
        <span className="meta">
          <bdi>{item.subject}</bdi> · {ownerLabel(item)}
        </span>
      </td>
      <td className="num">
        <span className={`pill ${pill.tone}`} data-deadline={pill.state}>
          {pill.label}
        </span>
      </td>
    </tr>
  );
}
